import { ReactNode } from "react";
import { createPortal } from "react-dom";

import { AddEditCompanyModal } from "../../../../route/shared/companies/panels/AddEditCompanyModal";
import { CompanyPicker, KanaTooltip } from "../../../../widget";
import { CompanyLink, useTransactionCompanyPicker } from "./useTransactionCompanyPicker";

export type { CompanyLink };

const typeRestrictionText = (collaboratorTypes: string[]): string => {
  const types =
    collaboratorTypes.length > 1
      ? `${collaboratorTypes.slice(0, -1).join(", ")} or ${collaboratorTypes[collaboratorTypes.length - 1]}`
      : collaboratorTypes[0];

  return `Only companies with the company type ${types} can be selected.`;
};

export interface TransactionCompanyPickerProps {
  label: string;
  // Whether the host panel is open — the company list is primed on open rather than on mount.
  show: boolean;
  /*
    The company currently linked. uuid is null in two cases: a transaction saved before company links existed,
    and — far more commonly — one whose company the viewer lacks MANAGE_COLLABORATOR on, since
    searchIssuanceTransactions returns the name but withholds the uuid (BR-02833). Either way the field shows
    that name rather than forcing a re-selection (BR-02831/02832), and submitting the null back is understood
    by the server as "unchanged" rather than as clearing the link.
  */
  selectedUuid: string | null;
  selectedName: string | null;
  // The company types this field offers — see CollaboratorTypeConstants. Drives both the search and the tooltip.
  collaboratorTypes: string[];
  disabled?: boolean;
  // Overrides the generated "only these company types" tooltip.
  tooltip?: ReactNode;
  variant?: "hub" | "seed";
  onChange: (company: CompanyLink | null) => void;
}

export const TransactionCompanyPicker = ({
  label,
  show,
  selectedUuid,
  selectedName,
  collaboratorTypes,
  disabled,
  tooltip,
  variant = "seed",
  onChange,
}: TransactionCompanyPickerProps): JSX.Element => {
  const {
    companySelectData,
    isLoading,
    showAddCompanyModal,
    pendingCompanyName,
    handleTypeSearch,
    handleSelect,
    handleAddNewCompany,
    handleCloseAddCompanyModal,
    handleCompanyCreated,
  } = useTransactionCompanyPicker({ show, collaboratorTypes, onChange });

  return (
    <>
      <CompanyPicker
        label={label}
        initialKey={selectedUuid}
        initialValue={selectedName}
        data={companySelectData}
        loading={isLoading}
        disabled={disabled}
        tooltip={tooltip ?? <KanaTooltip tooltipHeader="" tooltipText={typeRestrictionText(collaboratorTypes)} />}
        variant={variant}
        onTypeSearch={handleTypeSearch}
        onSelect={handleSelect}
        onAddNewCompany={handleAddNewCompany}
      />
      {/*
        Portalled to the body because this field sits inside the transaction panel's form and the add-company
        overlay carries a form of its own. Nested forms are invalid HTML — the browser drops the inner one, so
        saving a company would submit the transaction instead. The overlay is position: fixed, so moving it out
        of the panel does not change where it appears.

        The portal alone is not enough: React dispatches synthetic events along the fiber tree rather than the
        DOM, so the overlay's submit still reaches this form. useAddEditCompanyModal stops it propagating.
      */}
      {showAddCompanyModal &&
        createPortal(
          <AddEditCompanyModal
            show={showAddCompanyModal}
            mode="add"
            initialName={pendingCompanyName}
            /*
              The type this field searches on, so a company added here is findable from it later. A field
              offering a single type decides it outright and shows it read-only, since any other type would
              create a company this field could never select. A field taking several types only seeds the first.
            */
            initialCollaboratorType={collaboratorTypes[0]}
            lockCollaboratorType={collaboratorTypes.length === 1}
            onClose={handleCloseAddCompanyModal}
            onSaved={handleCompanyCreated}
          />,
          document.body
        )}
    </>
  );
};
